"use client";

import { c as _c } from "react/compiler-runtime";
import { RiBlurOffLine, RiImageLine } from "react-icons/ri";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Tooltip from "@/components/ui/tooltip";
import { TRANSPARENT_CHECKER } from "@/lib/photo-studio/studio-photo";
import { cx } from "@/lib/tailwind-utils";
import { STUDIO_BACKDROPS, STUDIO_BACKDROP_LABELS, type StudioBackdrop } from "@/types/photo-studio";
const CLINIC_DEFAULT = "CLINIC_DEFAULT";

// The swatch shows the backdrop's literal colour, so it is data, not theme.
const SWATCH_COLORS: Partial<Record<StudioBackdrop, string>> = {
  BLACK: "#000000",
  WHITE: "#FFFFFF",
  GREY: "#808080"
};
export function BackdropSwatch(t0) {
  const $ = _c(18);
  const {
    backdrop,
    brandColor,
    className,
    iconClassName: t1
  } = t0;
  const iconClassName = t1 === undefined ? "size-3" : t1;
  let base;
  let t2;
  if ($[0] !== backdrop || $[1] !== className || $[2] !== iconClassName) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      base = cx("inline-flex size-4 shrink-0 items-center justify-center rounded-full border border-border", className);
      if (backdrop === "NONE") {
        const t3 = cx(base, "bg-background");
        let t4;
        if ($[5] !== iconClassName) {
          t4 = cx(iconClassName, "text-foreground-secondary");
          $[5] = iconClassName;
          $[6] = t4;
        } else {
          t4 = $[6];
        }
        let t5;
        if ($[7] !== t4) {
          t5 = <RiImageLine className={t4} />;
          $[7] = t4;
          $[8] = t5;
        } else {
          t5 = $[8];
        }
        t2 = <span className={t3} aria-hidden={true}>{t5}</span>;
        break bb0;
      }
      if (backdrop === "BLUR") {
        const t3 = cx(base, "bg-background");
        let t4;
        if ($[9] !== iconClassName) {
          t4 = cx(iconClassName, "text-foreground-secondary");
          $[9] = iconClassName;
          $[10] = t4;
        } else {
          t4 = $[10];
        }
        let t5;
        if ($[11] !== t4) {
          t5 = <RiBlurOffLine className={t4} />;
          $[11] = t4;
          $[12] = t5;
        } else {
          t5 = $[12];
        }
        t2 = <span className={t3} aria-hidden={true}>{t5}</span>;
        break bb0;
      }
    }
    $[0] = backdrop;
    $[1] = className;
    $[2] = iconClassName;
    $[3] = base;
    $[4] = t2;
  } else {
    base = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const background = backdrop === "TRANSPARENT" ? TRANSPARENT_CHECKER : backdrop === "BRAND" ? brandColor ?? "#808080" : SWATCH_COLORS[backdrop];
  let t3;
  if ($[13] !== background) {
    t3 = {
      background
    };
    $[13] = background;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== base || $[16] !== t3) {
    t4 = <span className={base} style={t3} aria-hidden={true} />;
    $[15] = base;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}

// Swatch order in the row: the photo as taken, then the default (transparent),
// then the solid colours, then the photo's own background blurred.
const SWATCH_ORDER: StudioBackdrop[] = ["NONE", "TRANSPARENT", "WHITE", "GREY", "BLACK", "BRAND", "BLUR"];
interface BackdropSwatchesProps {
  // The backdrop the photo is shown on right now.
  value: StudioBackdrop;
  onChange: (backdrop: StudioBackdrop) => void;
  brandColor?: string | null;
  // Marks the swatch that is the clinic default in its tooltip.
  clinicDefault?: StudioBackdrop;
  exclude?: StudioBackdrop[];
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}

// One row of round swatches: Original plus every backdrop. Picking one shows
// the photo that way straight away.
export function BackdropSwatches(t0) {
  const $ = _c(29);
  const {
    value,
    onChange,
    brandColor,
    clinicDefault,
    exclude: t1,
    disabled,
    className,
    "aria-label": t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const exclude = t3;
  const ariaLabel = t2 === undefined ? "Backdrop" : t2;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[2] !== ariaLabel || $[3] !== brandColor || $[4] !== className || $[5] !== clinicDefault || $[6] !== disabled || $[7] !== exclude || $[8] !== onChange || $[9] !== value) {
    let t8;
    if ($[14] !== exclude) {
      t8 = b => !exclude.includes(b);
      $[14] = exclude;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    const options = SWATCH_ORDER.filter(t8);
    t4 = "radiogroup";
    t5 = ariaLabel;
    if ($[16] !== className) {
      t6 = cx("inline-flex items-center gap-1.5", className);
      $[16] = className;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t9;
    if ($[18] !== brandColor || $[19] !== clinicDefault || $[20] !== disabled || $[21] !== onChange || $[22] !== value) {
      t9 = backdrop => {
        const active = backdrop === value;
        const label = backdrop === clinicDefault && backdrop !== "NONE" ? `${STUDIO_BACKDROP_LABELS[backdrop]} (clinic default)` : STUDIO_BACKDROP_LABELS[backdrop];
        return <Tooltip key={backdrop} content={label} triggerAsChild={true}><button type="button" role="radio" aria-checked={active} aria-label={label} disabled={disabled} onClick={() => onChange(backdrop)} className={cx("inline-flex shrink-0 items-center justify-center rounded-full p-0.5 leading-none ring-offset-2 ring-offset-background transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70", active ? "ring-2 ring-primary" : "hover:ring-1 hover:ring-border", disabled && "cursor-not-allowed opacity-50")}><BackdropSwatch backdrop={backdrop} brandColor={brandColor} className="size-6" iconClassName="size-3.5" /></button></Tooltip>;
      };
      $[18] = brandColor;
      $[19] = clinicDefault;
      $[20] = disabled;
      $[21] = onChange;
      $[22] = value;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t7 = options.map(t9);
    $[2] = ariaLabel;
    $[3] = brandColor;
    $[4] = className;
    $[5] = clinicDefault;
    $[6] = disabled;
    $[7] = exclude;
    $[8] = onChange;
    $[9] = value;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7) {
    t8 = <div role={t4} aria-label={t5} className={t6}>{t7}</div>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}
interface BackdropPickerProps {
  value: StudioBackdrop | null;
  onChange: (backdrop: StudioBackdrop | null) => void;
  brandColor?: string | null;
  // Offer "Clinic default" (null). Its label names what the default is.
  clinicDefault?: StudioBackdrop;
  exclude?: StudioBackdrop[];
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}
export function BackdropPicker(t0) {
  const $ = _c(47);
  const {
    value,
    onChange,
    brandColor,
    clinicDefault,
    exclude: t1,
    disabled,
    className,
    id,
    "aria-label": t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const exclude = t3;
  const ariaLabel = t2 === undefined ? "Backdrop" : t2;
  let T0;
  let T1;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== ariaLabel || $[3] !== brandColor || $[4] !== className || $[5] !== clinicDefault || $[6] !== disabled || $[7] !== exclude || $[8] !== id || $[9] !== onChange || $[10] !== value) {
    let t10;
    if ($[19] !== exclude) {
      t10 = b => !exclude.includes(b);
      $[19] = exclude;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    const options = STUDIO_BACKDROPS.filter(t10);
    const allowDefault = clinicDefault !== undefined;
    const selected = value === null && allowDefault ? CLINIC_DEFAULT : value ?? "NONE";
    T1 = Select;
    t6 = selected;
    if ($[21] !== onChange) {
      t7 = next => onChange(next === CLINIC_DEFAULT ? null : next as StudioBackdrop);
      $[21] = onChange;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    t8 = disabled;
    let t11;
    if ($[23] !== className) {
      t11 = cx("min-w-40", className);
      $[23] = className;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    let t12;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <SelectValue />;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    if ($[26] !== ariaLabel || $[27] !== id || $[28] !== t11) {
      t9 = <SelectTrigger id={id} aria-label={ariaLabel} className={t11}>{t12}</SelectTrigger>;
      $[26] = ariaLabel;
      $[27] = id;
      $[28] = t11;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    T0 = SelectContent;
    if ($[30] !== allowDefault || $[31] !== brandColor || $[32] !== clinicDefault) {
      t4 = allowDefault && <SelectItem value={CLINIC_DEFAULT}><span className="flex items-center gap-2"><BackdropSwatch backdrop={clinicDefault} brandColor={brandColor} />Clinic default ({STUDIO_BACKDROP_LABELS[clinicDefault]})</span></SelectItem>;
      $[30] = allowDefault;
      $[31] = brandColor;
      $[32] = clinicDefault;
      $[33] = t4;
    } else {
      t4 = $[33];
    }
    let t13;
    if ($[34] !== brandColor) {
      t13 = backdrop => <SelectItem key={backdrop} value={backdrop}><span className="flex items-center gap-2"><BackdropSwatch backdrop={backdrop} brandColor={brandColor} />{STUDIO_BACKDROP_LABELS[backdrop]}</span></SelectItem>;
      $[34] = brandColor;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    t5 = options.map(t13);
    $[2] = ariaLabel;
    $[3] = brandColor;
    $[4] = className;
    $[5] = clinicDefault;
    $[6] = disabled;
    $[7] = exclude;
    $[8] = id;
    $[9] = onChange;
    $[10] = value;
    $[11] = T0;
    $[12] = T1;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    T0 = $[11];
    T1 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t10;
  if ($[36] !== T0 || $[37] !== t4 || $[38] !== t5) {
    t10 = <T0>{t4}{t5}</T0>;
    $[36] = T0;
    $[37] = t4;
    $[38] = t5;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== T1 || $[41] !== t10 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t11 = <T1 value={t6} onValueChange={t7} disabled={t8}>{t9}{t10}</T1>;
    $[40] = T1;
    $[41] = t10;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  return t11;
}